import type { ReactNode, Ref } from "preact/compat";
import { useEffect, useRef, useState } from "preact/hooks";
import { mergeRefs } from "react-merge-refs";
import { clsx } from "clsx";
import { compact } from "lodash-es";
import type { ButtonProps } from "~/components/button.tsx";
import Button from "~/components/button.tsx";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import CloseIcon from "./icons/cross.tsx";
import classes from "./modal.module.css";

type ActionButtonProps = ButtonProps & {
	readonly key: string;
};

type ModalProps = {
	readonly id: string;
	readonly dialogRef?: Ref<HTMLDialogElement>;
	readonly size: "small" | "medium" | "large" | "full";
	readonly showModalOnMount?: boolean;
	/** @deprecated Needs a rework/check */
	readonly cancelActionButton?: boolean | string;
	/** @deprecated Needs a rework/check */
	readonly actionButtons?: readonly ActionButtonProps[];
	readonly working?: boolean;
	readonly bodyClassName?: string;
	readonly children: ReactNode;
};

function Modal({
	id,
	dialogRef: providedDialogRef,
	showModalOnMount,
	cancelActionButton,
	actionButtons,
	bodyClassName,
	size,
	working,
	children,
}: ModalProps) {
	const workingRef = useRef(working);
	workingRef.current = working;

	const [dialogRef, setDialogRef] = useState<HTMLDialogElement | null>(null);
	useEffect(() => {
		if (dialogRef === null) {
			return;
		}

		function onCancel(e: Event) {
			e.preventDefault();
			dialogRef?.close();
		}

		dialogRef.addEventListener("cancel", onCancel);

		return () => {
			dialogRef.removeEventListener("cancel", onCancel);
		};
	}, [dialogRef]);

	const t = useTranslator();
	const allActions: readonly ActionButtonProps[] = compact([
		cancelActionButton && {
			key: "cancel",
			children:
				typeof cancelActionButton === "string"
					? cancelActionButton
					: t("Cancel"),
			variant: "black-line" as const,
			type: "button" as const,
			commandfor: id,
			command: "close",
			disabled: working,
		},
		...(actionButtons ?? []),
	]);

	// Show on mount
	const showModalOnMountRef = useRef(showModalOnMount);
	useEffect(() => {
		if (!showModalOnMountRef.current || dialogRef === null) {
			return;
		}

		dialogRef.showModal();
	}, [dialogRef]);

	return (
		<dialog
			id={id}
			ref={mergeRefs([setDialogRef, providedDialogRef])}
			className={clsx(classes["modal"], classes[`modal-${size}`])}
		>
			<div className={classes["modal-container"]}>
				<button
					className={classes["close-button"]}
					aria-label={t("Close")}
					type="button"
					disabled={working}
					commandfor={id}
					command="close"
				>
					<CloseIcon />
				</button>
				<div className={classes["modal-main"]}>
					<div className={clsx(classes["modal-body"], bodyClassName)}>
						{children}
					</div>
					{allActions.length > 0 && (
						<div className={classes["modal-actions"]}>
							{allActions.map(({ key: actionKey, ...a }) => (
								<Button key={actionKey} {...a} />
							))}
						</div>
					)}
				</div>
			</div>
		</dialog>
	);
}

export type { ModalProps };
export default Modal;
